import React, { useState } from "react";
import { useNavigate, Navigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { LogIn, ArrowLeft } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../i18n";

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  if (detail?.msg) return detail.msg;
  return String(detail);
}

export default function AdminLogin() {
  const { t } = useLang();
  const { user, ready, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  if (ready && user) return <Navigate to="/admin" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back");
      navigate("/admin");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center px-5">
      <div className="w-full max-w-md">
        <Link to="/" className="inline-flex items-center gap-2 font-mono2 text-xs uppercase text-neutral-500 hover:text-white mb-8" data-testid="back-home">
          <ArrowLeft size={14} /> Home
        </Link>
        <div className="border border-white/10 bg-[#0a0a0a] p-8">
          <img src="/logo.png" alt="JJ Truck Repair" className="h-16 w-auto object-contain mb-6" />
          <h1 className="font-display uppercase text-3xl text-white mb-1">{t.admin.login}</h1>
          <p className="font-body text-sm text-neutral-500 mb-8">Manage your website content.</p>

          <form onSubmit={submit} className="space-y-5">
            <div>
              <label className="font-mono2 text-xs uppercase text-neutral-400 block mb-2">{t.admin.email}</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full bg-black border border-white/15 px-4 py-3 text-white font-body focus:outline-none focus:border-primary-red focus:ring-2 focus:ring-[#E10600]/40"
                data-testid="login-email"
              />
            </div>
            <div>
              <label className="font-mono2 text-xs uppercase text-neutral-400 block mb-2">{t.admin.password}</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="w-full bg-black border border-white/15 px-4 py-3 text-white font-body focus:outline-none focus:border-primary-red focus:ring-2 focus:ring-[#E10600]/40"
                data-testid="login-password"
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              className="w-full flex items-center justify-center gap-2 bg-primary-red text-white font-body font-bold uppercase tracking-wide py-3.5 hover:bg-[#FF1E16] transition-colors disabled:opacity-60"
              data-testid="login-submit"
            >
              <LogIn size={18} />
              {loading ? t.admin.loggingIn : t.admin.signin}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
